/**
 * QuickReplyPicker - Seletor de respostas rápidas.
 * Aparece quando o usuário digita "/" no campo de mensagem.
 * 
 * Princípios aplicados:
 * - Single Responsibility: Apenas UI/lógica do seletor de respostas rápidas
 * - DRY: Reutiliza hook useQuickReplies existente
 */

import React, { useMemo, useEffect, useRef, useState } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { useQuickReplies } from '../../../settings/quick-replies/hooks/useQuickReplies';

interface QuickReplyPickerProps {
    searchTerm: string;
    onSelect: (message: string) => void;
    onClose: () => void;
}

export const QuickReplyPicker: React.FC<QuickReplyPickerProps> = ({
    searchTerm,
    onSelect,
    onClose
}) => {
    const { replies, loading } = useQuickReplies();
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Filtra respostas rápidas pelo termo de busca
    const filteredReplies = useMemo(() => {
        if (!searchTerm) return replies;
        const lowerTerm = searchTerm.toLowerCase();
        return replies.filter(reply =>
            reply.shortcut?.toLowerCase().includes(lowerTerm) ||
            reply.message?.toLowerCase().includes(lowerTerm)
        );
    }, [replies, searchTerm]);

    // Reset índice quando filtro muda
    useEffect(() => {
        setSelectedIndex(0);
    }, [filteredReplies.length]);

    // Navegação por teclado
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < filteredReplies.length - 1 ? prev + 1 : prev
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
            } else if (e.key === 'Enter' && filteredReplies.length > 0) {
                e.preventDefault();
                onSelect(filteredReplies[selectedIndex].message);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [filteredReplies, selectedIndex, onSelect, onClose]);

    // Fecha ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    if (loading) {
        return (
            <div
                ref={containerRef}
                className="absolute bottom-full left-0 mb-2 w-64 bg-card rounded-lg shadow-lg border border-subtle py-3"
            >
                <div className="flex items-center justify-center gap-2">
                    <AppIcon name="loader" className="w-4 h-4 animate-spin text-blue-500" />
                    <span className="text-xs text-muted">Carregando...</span>
                </div>
            </div>
        );
    }

    if (filteredReplies.length === 0) {
        return (
            <div
                ref={containerRef}
                className="absolute bottom-full left-0 mb-2 w-64 bg-card rounded-lg shadow-lg border border-subtle py-3"
            >
                <p className="text-xs text-muted text-center">
                    {searchTerm ? 'Nenhuma encontrada' : 'Sem respostas rápidas'}
                </p>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="absolute bottom-full left-0 mb-2 w-72 bg-card rounded-lg shadow-lg border border-border overflow-hidden z-50"
        >
            {/* Header compacto */}
            <div className="px-3 py-1.5 border-b border-subtle">
                <span className="text-xs font-medium text-muted">Respostas rápidas</span>
            </div>

            {/* Lista */}
            <div className="max-h-48 overflow-y-auto py-1">
                {filteredReplies.map((reply, index) => (
                    <button
                        key={reply.id}
                        onClick={() => onSelect(reply.message)}
                        className={`w-full text-left px-3 py-2 transition-colors ${index === selectedIndex
                            ? 'bg-primary/10'
                            : 'hover:bg-surface-subtle'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            {reply.shortcut && (
                                <span className="text-xs font-mono text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                    /{reply.shortcut}
                                </span>
                            )}
                            <span className="text-sm text-secondary truncate">
                                {reply.message}
                            </span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};
